import {isNotNil} from 'ramda';

import {Locale} from '@dms/api/base';

import {AutomaticMessageSettingsValue} from '../types/AutomaticMessageSettingsValue';
import {ContextTemplateRecords} from '../types/FormValues';

// Accumulators are prototype-less: the locale keys come from a stored payload this
// screen does not control.
const buildRecord = <TItem extends {locale: string}>(
  locales: Locale[],
  items: TItem[] | undefined,
  pick: (item: TItem) => string | undefined
): Record<string, string> => {
  const byLocale = new Map<string, TItem>((items ?? []).map((item) => [item.locale, item]));
  const localesSet = new Set<string>(locales);

  // The save replaces the whole array, so locales the tenant no longer has are kept
  // verbatim — dropping them here would delete copy written while they were still active.
  const orphanedEntries = (items ?? [])
    .filter((item) => !localesSet.has(item.locale))
    .reduce<Record<string, string>>((accumulator, item) => {
      accumulator[item.locale] = pick(item) ?? '';
      return accumulator;
    }, Object.create(null));

  // Every rendered locale needs a key so its field renders even when nothing is stored.
  const localeEntries = locales.reduce<Record<string, string>>((accumulator, locale) => {
    const item = byLocale.get(locale);
    accumulator[locale] = (isNotNil(item) ? pick(item) : undefined) ?? '';
    return accumulator;
  }, Object.create(null));

  return {...orphanedEntries, ...localeEntries};
};

export const buildContextTemplatesRecord = (
  locales: Locale[],
  value: AutomaticMessageSettingsValue | undefined
): ContextTemplateRecords => ({
  emailSubjects: buildRecord(locales, value?.emailContextTemplates, (item) => item.subject),
  emailBodies: buildRecord(locales, value?.emailContextTemplates, (item) => item.body),
  smsBodies: buildRecord(locales, value?.smsContextTemplates, (item) => item.body),
});
